/**
 * Groups API request/response DTOs (backend shapes).
 */
import type { ProblemDetails } from '../apiTypes';

/**
 * Backend group role IDs, still returned inside `groupRole` strings on some
 * rows. AddMember / ChangeMemberRole take the named role (`GroupRoleType`), not
 * these ids.
 */
export const GROUP_ROLE_ID = {
  Owner: 1,
  Admin: 2,
  Member: 3,
} as const;

export type GroupRoleId = (typeof GROUP_ROLE_ID)[keyof typeof GROUP_ROLE_ID];

/** `GroupRoleType` in the API document — the wire format for role changes. */
export const GROUP_ROLE = {
  Owner: 'Owner',
  Admin: 'Admin',
  Member: 'Member',
} as const;

export type GroupRoleType = (typeof GROUP_ROLE)[keyof typeof GROUP_ROLE];

export type ApiGroupSortBy =
  | 'Newest'
  | 'Oldest'
  | 'MostMembers'
  | 'AToZ'
  | 'ZToA';

/** `GroupTypeFilterOption` — narrows the list to public or private groups. */
export type ApiGroupTypeFilter = 'All' | 'Public' | 'Private';

export interface GetGroupsQueryParams {
  SearchTerm?: string;
  PageNumber?: number;
  PageSize?: number;
  SortBy?: ApiGroupSortBy;
  GroupType?: ApiGroupTypeFilter;
}

export interface ApiGroupListItem {
  row?: number;
  groupId: number;
  ownerUserId?: number;
  name: string;
  description?: string;
  iconUrl?: string;
  bannerUrl?: string;
  isPrivate?: boolean;
  requireJoinApproval?: boolean;
  requirePostApproval?: boolean;
  onlyAdminCanPost?: boolean;
  maxMembers?: number;
  memberCount?: number;
  createdAt?: string;
  isActive?: boolean;
  isDeleted?: boolean;
  firstPostDate?: string;
  lastPostDate?: string;
  /** @deprecated Prefer memberStatus */
  membershipStatus?: string;
  memberStatus?: 'notMember' | 'joined' | 'pending' | string;
  isOwner?: boolean;
  isAdmin?: boolean;
}

export interface ApiPaginatedGroups {
  items: ApiGroupListItem[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  message?: string;
}

export interface ApiMyGroupItem {
  groupId: number;
  name: string;
  description?: string;
  iconUrl?: string;
  bannerUrl?: string;
  isPrivate?: boolean;
  memberCount?: number;
  createdAt?: string;
  /** @deprecated Prefer memberStatus */
  membershipStatus?: string;
  memberStatus?: 'notMember' | 'joined' | 'pending' | string;
  isOwner?: boolean;
  isAdmin?: boolean;
}

export interface ApiGroupMutationEnvelope<T = ApiGroupRecord> {
  isSuccess?: boolean;
  success?: boolean;
  message?: string;
  errors?: string[];
  data?: T;
}

export interface ApiGroupRecord {
  groupId: number;
  ownerUserId?: number;
  name: string;
  description?: string;
  iconUrl?: string;
  bannerUrl?: string;
  isPrivate?: boolean;
  requireJoinApproval?: boolean;
  requirePostApproval?: boolean;
  onlyAdminCanPost?: boolean;
  maxMembers?: number;
}

export interface CreateGroupRequest {
  name: string;
  description: string;
  iconUrl?: string;
  bannerUrl?: string;
  isPrivate: boolean;
  requireJoinApproval: boolean;
  requirePostApproval: boolean;
  onlyAdminCanPost: boolean;
  maxMembers?: number;
}

export interface UpdateGroupRequest {
  groupId: number;
  name: string;
  description: string;
  iconUrl?: string;
  bannerUrl?: string;
  requireJoinApproval?: boolean;
  isPrivate?: boolean;
  requirePostApproval?: boolean;
  onlyAdminCanPost?: boolean;
}

export interface GroupIdRequest {
  groupId: number;
  timezoneOffset?: number;
}

export interface AddMemberRequest {
  groupId: number;
  userId: number;
  roleId: GroupRoleType;
}

export interface ChangeMemberRoleRequest {
  groupId: number;
  userId: number;
  newRoleId: GroupRoleType;
}

export interface RemoveMemberRequest {
  groupId: number;
  userId: number;
}

export type JoinRequestReviewAction = 'Approve' | 'Reject' | string;

export interface ReviewJoinRequestPayload {
  groupJoinRequestId: number;
  action: JoinRequestReviewAction;
  /** Optional admin note when approving or rejecting. */
  reviewComments?: string;
}

export interface GetGroupJoinRequestsPayload {
  groupId: number;
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
}

export interface ApiGroupJoinRequestItem {
  groupJoinRequestId: number;
  groupId: number;
  requestedByUserId: number;
  userGuid?: string;
  profileId?: number;
  firstName?: string;
  userName?: string;
  profileImage?: string;
  requestedAt?: string;
}

export interface ApiPaginatedJoinRequests {
  items: ApiGroupJoinRequestItem[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  message?: string;
}

export interface ApiJoinRequestsEnvelope {
  isSuccess?: boolean;
  message?: string;
  errors?: string[];
  data?: ApiPaginatedJoinRequests;
}

export interface ApiGroupDetailRecord {
  groupId: number;
  groupName?: string;
  name?: string;
  ownerId?: number;
  ownerUserId?: number;
  ownerUserName?: string;
  ownerPhoto?: string;
  /** Owner's profile GUID, for the View Profile link. */
  profileUserGuid?: string;
  /** Backend DetailResponse documents this as int32 (0/1); some payloads use boolean. */
  isOwner?: boolean | number;
  isAdmin?: boolean;
  description?: string;
  iconUrl?: string;
  bannerUrl?: string;
  isPrivate?: boolean;
  requireJoinApproval?: boolean;
  requirePostApproval?: boolean;
  onlyAdminCanPost?: boolean;
  isArchived?: boolean;
  createdAt?: string;
  membershipStatus?: string;
  currentUserMembershipStatus?: string;
  memberStatus?: 'notMember' | 'joined' | 'pending' | string;
  memberCount?: number;
  todayPostCount?: number;
  thisMonthPostCount?: number;
}

export interface ApiGroupMemberRecord {
  groupId?: number;
  memberId?: number;
  userId?: number;
  /** UUID used for View Profile navigation. */
  userGuid?: string;
  profileUserGuid?: string;
  profileId?: number;
  firstName?: string;
  userName?: string;
  userProfile?: string;
  profileImage?: string;
  groupRole?: string;
  createdAt?: string;
  isGroupAdmin?: boolean;
  /** Who approved the membership, and the approval state of the join. */
  approveName?: string;
  approveStatus?: string;
}

export interface ApiGroupActivityRecord {
  groupId?: number;
  membersCount?: number;
  newJoinedCount?: number;
  newJoinRequestCount?: number;
  newPostCount?: number;
  todayPostCount?: number;
  thisMonthPostCount?: number;
}

export interface ApiFeedItem {
  postId: number;
  groupId?: number;
  userId?: number;
  profileUserGuid?: string | null;
  userName?: string;
  firstName?: string;
  groupName?: string;
  userPhoto?: string;
  postDate?: string;
  commentCount?: number;
  reactedCount?: number;
  isReacted?: boolean | number;
  totalShares?: number;
  bodyText?: string;
  title?: string;
  url?: string;
  contentTypeName?: string;
  photoAlbum?: string[];
  thumbnailUrl?: string;
  currentWeight?: number;
  scaleUnitWeightName?: string;
  allowShare?: boolean;
  hashTags?: string[] | null;
}

export interface ApiGetGroupDetailResponse {
  statusCode?: number;
  message?: string;
  success?: boolean;
  groupDetail?: ApiGroupDetailRecord | ApiGroupDetailRecord[];
  groupMembers?: ApiGroupMemberRecord[];
  groupActivity?: ApiGroupActivityRecord | ApiGroupActivityRecord[];
  feeds?: ApiFeedItem[];
  membershipStatus?: string;
}

export interface GetGroupsListRequest {
  isMyGroup: boolean;
  searchTerm?: string;
}

export interface ApiGetGroupsListResponse {
  statusCode?: number;
  message?: string;
  success?: boolean;
  groups?: Array<{
    groupId: number;
    groupName: string;
    iconUrl?: string;
    membersCount?: number;
    isJoined?: boolean;
  }>;
}

export interface GetFeedsRequest {
  method: 'Groups' | string;
  startIndex?: number;
  pageSize?: number;
  search?: string;
  isMyGroup?: boolean;
  timezoneOffset?: number;
}

export interface ApiGetFeedsResponse {
  statusCode?: number;
  message?: string;
  success?: boolean;
  feeds?: ApiFeedItem[];
}

export interface ApiActionResponse {
  success?: boolean;
  status?: string;
  message?: string;
}

export type GroupsApiError = ProblemDetails | ApiGroupMutationEnvelope<unknown>;
